import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SectionBackground from '../components/SectionBackground';
import { WatchAlertMock } from '../components/BrazilWatchBand';

const CHECKOUT_URL = 'https://buy.stripe.com/5kQ00jaWY9767QG23n6EU03?locale=en';
const PORTFOLIO_MAILTO =
  'mailto:contact@tmrush.com?subject=Brazil%20Watch%20%E2%80%94%20portfolio%20rates';

const facts = [
  {
    value: 'Every Tuesday',
    label: "Brazil's trademark office publishes all its decisions in one weekly gazette, only in Portuguese.",
  },
  {
    value: '60 days',
    label: 'is the window to oppose a conflicting filing after it is published. Miss it and the fight gets slower and costlier.',
  },
  {
    value: 'First to file',
    label: 'Brazil gives a trademark to whoever files first, not to whoever used it first.',
  },
];

const steps = [
  {
    title: 'Tell us what to watch',
    text: 'Your brand name and, if you already filed in Brazil, the process number. Up to three classes per mark.',
  },
  {
    title: 'We read every gazette',
    text: 'Each Tuesday we check every newly published application against your mark, including names that only sound alike in Portuguese.',
  },
  {
    title: 'You get it in English',
    text: 'A Brazilian specialist reviews each possible conflict and sends you a clear alert with the opposition deadline.',
  },
  {
    title: 'One reply to act',
    text: 'Want to oppose? Our local team prepares and files it before the BPTO (INPI). Nothing is filed without your approval.',
  },
];

const included = [
  'Screening of newly published applications in every weekly gazette (RPI)',
  'Identical and similar marks, including phonetic matches in Portuguese',
  'Material conflict alerts reviewed by a Brazilian specialist',
  'Opposition deadline identified for every alert',
  'Concise preliminary recommendation in English',
  'One principal verbal element in up to three relevant Nice classes',
];

const faqs = [
  {
    q: 'Do I need to have filed with TM RUSH®?',
    a: 'No. We watch your brand against new Brazilian filings whoever handled your own application.',
  },
  {
    q: 'My brand is not in Brazil yet. Does this help?',
    a: 'Yes. Brazil is first-to-file, so knowing when someone else files your name is the first line of defense.',
  },
  {
    q: 'Who acts if I want to oppose?',
    a: 'TM RUSH®, through Marcas Já, files the opposition before the BPTO (INPI) as your local representative. Oppositions are quoted per class.',
  },
];

const BrazilWatchPage = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Brazil Trademark Watch | TM RUSH';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="bg-background min-h-screen">
      <SectionBackground
        image="/images/bg-hero.svg"
        overlay="dark"
        minHeight="min-h-[min(520px,70vh)]"
        className="pt-40 md:pt-48 pb-28 md:pb-32 lg:pb-36"
      >
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-14 items-center">
            <div className="xl:col-span-7 text-white text-left">
              <p className="text-primary-light font-semibold text-sm uppercase tracking-wider mb-4">
                Brazil Trademark Watch
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Know every Tuesday what happened to your brand in Brazil
              </h1>
              <p className="text-lg text-gray-200 leading-relaxed max-w-2xl mb-8">
                Brazil publishes every trademark decision in a weekly gazette, in Portuguese. We read it for you and
                alert you in plain English when someone files a mark like yours, while there is still time to act.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={CHECKOUT_URL}
                  className="inline-block bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
                >
                  Start watching · US$ 150/year
                </a>
                <a
                  href="#how-it-works"
                  className="inline-block bg-white/10 text-white px-8 py-3 rounded-xl hover:bg-white/20 font-semibold border border-white/25 backdrop-blur-sm"
                >
                  How it works
                </a>
              </div>
            </div>
            <div className="xl:col-span-5 w-full max-w-md xl:ml-auto">
              <WatchAlertMock />
            </div>
          </div>
        </div>
      </SectionBackground>

      <section className="bg-background-auxiliary py-16 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-10">Why Brazil needs watching</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {facts.map((f) => (
              <div key={f.value} className="bg-background-white rounded-2xl p-8 border border-gray-100/80 shadow-sm">
                <div className="text-3xl font-bold text-primary mb-3">{f.value}</div>
                <p className="text-text/70 leading-relaxed">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="container mx-auto px-4 py-16 md:py-20 scroll-mt-24">
        <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">How it works</h2>
        <p className="text-text/80 text-lg max-w-3xl mb-10">
          Set it up once. After that, Brazil comes to your inbox every Tuesday.
        </p>
        <div className="max-w-4xl rounded-3xl overflow-hidden shadow-xl border border-gray-100 bg-text mb-10">
          <video
            src="/videos/tmrush-brazil-watch.mp4"
            poster="/videos/tmrush-brazil-watch-poster.jpg"
            controls
            muted
            playsInline
            preload="metadata"
            className="w-full h-auto block"
            aria-label="One-minute video: how TM RUSH Brazil Watch works"
          />
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <li key={s.title} className="bg-background-white rounded-2xl p-8 border border-gray-100/80 shadow-sm">
              <span className="w-10 h-10 rounded-full bg-text text-white font-bold flex items-center justify-center mb-5">
                {i + 1}
              </span>
              <h3 className="text-lg font-bold text-text mb-2">{s.title}</h3>
              <p className="text-text/70 leading-relaxed text-sm">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-background-auxiliary py-16 md:py-20">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">Software that reads. People who act.</h2>
            <p className="text-text/80 text-lg leading-relaxed max-w-xl">
              Watch tools send you raw matches. We send you what matters, checked by a Brazilian trademark specialist,
              with the deadline counted and the next step ready.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {included.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-text/80 bg-background-white rounded-lg p-4 border border-gray-100/80"
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold mt-0.5">
                  ✓
                </span>
                <span className="leading-relaxed text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="watch-pricing" className="container mx-auto px-4 py-16 md:py-20 scroll-mt-24">
        <h2 className="text-3xl md:text-4xl font-bold text-text mb-4">Simple pricing</h2>
        <p className="text-text/80 text-lg max-w-3xl mb-10">
          One watch covers one brand name in up to three classes, for 12 months.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <div className="lg:col-span-1 bg-text text-white rounded-2xl p-8 shadow-md flex flex-col">
            <h3 className="text-xl font-bold mb-2">Brazil Trademark Watch</h3>
            <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
              <span className="text-4xl md:text-5xl font-bold whitespace-nowrap">US$ 150</span>
              <span className="text-gray-300 whitespace-nowrap">per mark / year</span>
            </div>
            <p className="text-gray-300 text-sm mb-6">Up to three classes · no official fees</p>
            <a
              href={CHECKOUT_URL}
              className="mt-auto inline-block text-center bg-primary text-white px-8 py-3 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
            >
              Start watching
            </a>
          </div>
          <div className="bg-background-white rounded-2xl p-8 border border-gray-100 shadow-sm flex flex-col">
            <h3 className="text-xl font-bold text-text mb-2">IP firms &amp; portfolios</h3>
            <div className="text-4xl font-bold text-text mb-1">Portfolio rates</div>
            <p className="text-text/70 text-sm mb-6">
              From five watched marks. Associate terms are agreed in writing before instruction.
            </p>
            <a
              href={PORTFOLIO_MAILTO}
              className="mt-auto inline-block text-center bg-background-auxiliary text-text px-8 py-3 rounded-xl hover:bg-gray-200 font-semibold"
            >
              Request portfolio rates
            </a>
          </div>
          <div className="bg-background-white rounded-2xl p-8 border border-gray-100 shadow-sm flex flex-col">
            <h3 className="text-xl font-bold text-text mb-2">Filing in Brazil with us?</h3>
            <div className="text-4xl font-bold text-text mb-1">12 months included</div>
            <p className="text-text/70 text-sm mb-6">
              Every new national filing through TM RUSH® comes with 12 months of Brazil Trademark Watch.
            </p>
            <Link
              to="/order"
              className="mt-auto inline-block text-center bg-background-auxiliary text-text px-8 py-3 rounded-xl hover:bg-gray-200 font-semibold"
            >
              Order online
            </Link>
          </div>
        </div>
        <p className="mt-6 text-sm text-text/60 max-w-3xl">
          Oppositions, legal opinions, online or figurative watch and enforcement measures are quoted separately.
          Alerts are information, not a guarantee of any outcome.
        </p>
      </section>

      <section className="bg-background-auxiliary py-16 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-10">Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {faqs.map((f) => (
              <div key={f.q} className="bg-background-white rounded-2xl p-8 border border-gray-100/80 shadow-sm">
                <h3 className="font-bold text-text mb-2">{f.q}</h3>
                <p className="text-text/70 leading-relaxed text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionBackground image="/images/bg-hero.svg" overlay="dark" className="py-16 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Would you know if someone filed your brand in Brazil today?
          </h2>
          <p className="text-gray-300 text-lg mb-8">Start watching now. Your first report follows the next Tuesday gazette.</p>
          <a
            href={CHECKOUT_URL}
            className="inline-block bg-primary text-white px-10 py-4 rounded-xl hover:bg-primary-hover font-semibold shadow-md"
          >
            Start watching · US$ 150/year
          </a>
        </div>
      </SectionBackground>
    </main>
  );
};

export default BrazilWatchPage;
